import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, Instagram, Facebook, Twitter } from 'lucide-react';
import { toast } from 'sonner';
import { useSiteSettings } from '../context/SiteSettingsContext';

const ContactPage = () => {
  const { settings } = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    // Simulate sending
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success("Message Sent!", {
      description: "We'll get back to you shortly."
    });
    
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-warm-white">
      {/* Hero Section */}
      <div className="bg-soft-sand py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 font-heading text-charcoal">Contact Us</h1>
          <p className="text-xl text-stone-grey font-body max-w-2xl mx-auto">
            We're here to help! Send us a message and we'll respond within 24 hours.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-charcoal mb-6 font-heading">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-muted-green/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-muted-green" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-charcoal font-body">Email</h3>
                    <a href={`mailto:${settings.email}`} className="text-muted-green hover:underline font-body">
                      {settings.email}
                    </a>
                    <p className="text-sm text-stone-grey font-body mt-1">General inquiries</p>
                    <a href={`mailto:${settings.legal_email}`} className="text-muted-green hover:underline font-body block mt-2">
                      {settings.legal_email}
                    </a>
                    <p className="text-sm text-stone-grey font-body mt-1">Legal inquiries</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-muted-green/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-muted-green" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-charcoal font-body">Phone</h3>
                    <a href={`tel:${settings.phone.replace(/\D/g, '')}`} className="text-muted-green hover:underline font-body text-lg">
                      {settings.phone}
                    </a>
                    <p className="text-sm text-stone-grey font-body mt-1">Toll-free (US)</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-muted-green/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-muted-green" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-charcoal font-body">Address</h3>
                    <p className="text-stone-grey font-body">
                      {settings.address.street}<br />
                      {settings.address.city}, {settings.address.state} {settings.address.zip}<br />
                      {settings.address.country}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-muted-green/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-muted-green" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-charcoal font-body">Business Hours</h3>
                    <p className="text-stone-grey font-body">
                      Mon-Fri: 9:00 AM - 6:00 PM (PST)<br />
                      Sat: 10:00 AM - 4:00 PM<br />
                      Sun: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h3 className="font-semibold text-charcoal mb-4 font-body">Follow Us</h3>
              <div className="flex gap-4">
                {settings.social_links.instagram && (
                  <a 
                    href={settings.social_links.instagram}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
                {settings.social_links.facebook && (
                  <a 
                    href={settings.social_links.facebook}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                )}
                {settings.social_links.twitter && (
                  <a 
                    href={settings.social_links.twitter}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                  >
                    <Twitter className="h-5 w-5" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 border border-soft-sand shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <MessageSquare className="h-6 w-6 text-muted-green" />
                <h2 className="text-2xl font-bold text-charcoal font-heading">Send Us a Message</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-2 font-body">
                      Your Name *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                      className="font-body"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-2 font-body">
                      Email *
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                      className="font-body"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-2 font-body">
                      Phone
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                      className="font-body"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal mb-2 font-body">
                      Subject *
                    </label>
                    <select
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-soft-sand rounded-md font-body bg-white"
                    >
                      <option value="">Select a subject</option>
                      <option value="order">Order Question</option>
                      <option value="product">Product Inquiry</option>
                      <option value="return">Return / Exchange</option>
                      <option value="shipping">Shipping</option>
                      <option value="partnership">Partnership</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2 font-body">
                    Message *
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us more about your inquiry..."
                    rows={6}
                    required
                    className="w-full px-3 py-2 border border-soft-sand rounded-md font-body resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-muted-green hover:bg-muted-green/90 text-white py-6 text-lg font-body"
                >
                  {sending ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* FAQ Teaser */}
        <div className="mt-16 text-center">
          <div className="bg-soft-sand/30 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-charcoal mb-4 font-heading">
              Frequently Asked Questions
            </h3>
            <p className="text-stone-grey font-body mb-6">
              You might find your answer in our FAQ section
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/shipping-policy" className="text-muted-green hover:underline font-body">Shipping</a>
              <span className="text-soft-sand">•</span>
              <a href="/return-policy" className="text-muted-green hover:underline font-body">Returns</a>
              <span className="text-soft-sand">•</span>
              <a href="/terms" className="text-muted-green hover:underline font-body">Terms</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
